import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { subscribeToNewsletter } from '../../services/newsletterService';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import './Footer.css';

const Footer = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [socialLinks, setSocialLinks] = useState([]);
  
  // Check if user is admin or subadmin
  const isAdmin = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';

  // Fetch social links from API
  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/settings/social-links/public`);
        if (response.ok) {
          const data = await response.json();
          // All returned links are already visible (filtered by API)
          setSocialLinks(data || []);
        }
      } catch (error) {
        console.error('Error fetching social links:', error);
      }
    };
    fetchSocialLinks();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage({ text: 'Please enter your email', type: 'error' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ text: 'Please enter a valid email', type: 'error' });
      return;
    }

    setIsLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await subscribeToNewsletter(email, null, 'footer');
      setMessage({ text: response.message || 'Successfully subscribed!', type: 'success' });
      setEmail('');
    } catch (error) {
      setMessage({ text: error.error || 'Failed to subscribe', type: 'error' });
    } finally {
      setIsLoading(false);
      // Clear message after 5 seconds
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-main-content">
        {/* Footer Columns Section */}
        <div className="footer-columns-container">
          {/* Our Services */}
          <div className="footer-column">
            <h4 className="footer-title">Our Services</h4>
            <ul className="footer-links">
              <li><span style={{ cursor: 'default', color: '#fff' }}>Advertise with us</span></li>
              <li><Link to={isAdmin ? "/admin/membership" : "/membership-packages"}>Membership Plan</Link></li>
              <li><Link to="/news">News</Link></li>
            </ul>
          </div>

          {/* Divider */}
          <div className="footer-vertical-divider"></div>

          {/* Suppliers Tools */}
          <div className="footer-column">
            <h4 className="footer-title">Suppliers Tools</h4>
            <ul className="footer-links">
              <li><Link to="/send-inquire">Post your Requirement</Link></li>
              <li><Link to="/documents-submit">Become a Supplier</Link></li>
              {/* TEMPORARILY HIDDEN - Buy Leads, Specialists, Projects
              <li><Link to="/buy-leads">Latest Buy leads</Link></li>
              <li><Link to="/specialists">Specialists</Link></li>
              <li><Link to="/projects">Projects</Link></li>
              END TEMPORARILY HIDDEN */}
              <li><Link to="/seller-faq">Seller FAQ</Link></li>
            </ul>
          </div>

          {/* Divider */}
          <div className="footer-vertical-divider"></div>

          {/* Quick Links */}
          <div className="footer-column">
            <h4 className="footer-title">Quick Links</h4>
            <ul className="footer-links">
              <li><Link to="/about-us">About Us</Link></li>
              <li><Link to="/contact-us">Contact Us</Link></li>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms-conditions">Terms & Conditions</Link></li>
            </ul>
            
            {/* Contact Info */}
            <div className="footer-contact-info">
              <div className="contact-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_whatsapp)">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12.7669 2.17987C11.3636 0.774914 9.49725 0.000820312 7.50898 0C3.41201 0 0.0776562 3.33426 0.0760156 7.43233C0.0754531 8.74237 0.417664 10.0212 1.06815 11.1484L0.0136719 15L3.95401 13.9664C5.03972 14.5586 6.26206 14.8707 7.50596 14.8711H7.5091C11.6056 14.8711 14.9403 11.5366 14.9419 7.43829C14.9427 5.45215 14.1703 3.58476 12.7669 2.17987ZM7.50898 13.6158H7.5064C6.3979 13.6153 5.31071 13.3174 4.36203 12.7546L4.13656 12.6207L1.7983 13.2341L2.42239 10.9543L2.27544 10.7206C1.65701 9.73696 1.33044 8.60008 1.331 7.43278C1.33229 4.02656 4.10377 1.25536 7.51147 1.25536C9.1616 1.2559 10.7128 1.89933 11.8792 3.06708C13.0455 4.23483 13.6875 5.78702 13.687 7.43782C13.6855 10.8443 10.9142 13.6158 7.50898 13.6158ZM10.8977 8.98879C10.712 8.89579 9.79889 8.44664 9.6286 8.38457C9.45849 8.32258 9.33453 8.29171 9.21085 8.47757C9.08698 8.66343 8.7311 9.08182 8.62271 9.20568C8.51431 9.32967 8.4061 9.34521 8.22033 9.25221C8.03459 9.15928 7.43618 8.96308 6.72675 8.33036C6.17468 7.83789 5.80198 7.22972 5.69358 7.04388C5.58537 6.85783 5.69265 6.76701 5.77505 6.66482C5.9761 6.41517 6.17743 6.15342 6.23933 6.02955C6.30129 5.90557 6.27026 5.7971 6.22376 5.70417C6.17743 5.61124 5.80601 4.69708 5.65128 4.32511C5.50041 3.96312 5.34741 4.01198 5.23334 4.00633C5.12513 4.00092 5.00126 3.99982 4.8774 3.99982C4.7536 3.99982 4.55236 4.04622 4.38209 4.23227C4.21189 4.41822 3.73217 4.86747 3.73217 5.78163C3.73217 6.69579 4.39765 7.57889 4.49049 7.70285C4.5833 7.82683 5.80015 9.70272 7.66315 10.5071C8.10626 10.6987 8.45215 10.8128 8.72196 10.8984C9.1669 11.0398 9.57167 11.0198 9.89173 10.972C10.2486 10.9186 10.9905 10.5227 11.1454 10.0889C11.3001 9.65505 11.3001 9.28324 11.2536 9.20568C11.2073 9.12825 11.0834 9.08182 10.8977 8.98879Z" fill="#F5F5F5"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_whatsapp">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                <span>009715444586500</span>
              </div>
              <div className="contact-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_email)">
                    <path d="M13.4443 2.7041L13.6211 2.71289C14.0309 2.75417 14.4161 2.93577 14.71 3.22949C15.0456 3.56516 15.2353 4.02042 15.2363 4.49512V11.5049C15.2358 11.9798 15.0468 12.4356 14.7109 12.7715C14.417 13.0654 14.0311 13.2461 13.6211 13.2871L13.4443 13.2959H2.55566C2.0808 13.2954 1.6249 13.1072 1.28906 12.7715C0.953216 12.4356 0.764202 11.9798 0.763672 11.5049V4.49512L0.772461 4.31836C0.813775 3.90857 0.995288 3.52327 1.28906 3.22949C1.62476 2.8938 2.07996 2.70498 2.55469 2.7041H13.4443ZM9.34473 8.94043C8.53937 9.5566 7.46275 9.55637 6.6543 8.94043L2.50195 12.374C2.51974 12.3753 2.53768 12.3799 2.55566 12.3799H13.4434C13.4611 12.3797 13.4785 12.3753 13.4961 12.374L9.34473 8.94043ZM1.71387 4.2666C1.69253 4.34008 1.68066 4.41687 1.68066 4.49609V11.5039L1.68848 11.6201C1.69787 11.6869 1.71664 11.7517 1.74121 11.8145L5.9668 8.32031L1.71387 4.2666ZM10.0322 8.32031L14.2568 11.8145C14.2953 11.7174 14.3193 11.614 14.3193 11.5049V4.49609C14.3193 4.41675 14.3055 4.34015 14.2842 4.2666L10.0322 8.32031ZM2.55469 3.62012C2.49589 3.62013 2.43845 3.62611 2.38281 3.6377L7.08887 8.12402C7.33431 8.35786 7.661 8.48828 8 8.48828C8.33884 8.48817 8.66482 8.35776 8.91016 8.12402L13.6133 3.63867C13.5585 3.62749 13.5022 3.6211 13.4443 3.62109L2.55469 3.62012Z" fill="#F5F5F5" stroke="#F5F5F5" strokeWidth="0.25"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_email">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                <span>Info@gulfconstructor.com</span>
              </div>
            </div>
          </div>

          {/* Newsletter Section */}
          <div className="footer-newsletter-section">
            <div className="newsletter-content">
              <div className="newsletter-text-content">
                <h4 className="newsletter-title">NEWSLETTER</h4>
                <p className="newsletter-description">Subscribe to our new channel to get latest updates</p>
              </div>
              <form className="newsletter-form" onSubmit={handleSubscribe}>
                <input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="newsletter-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
                <button 
                  type="submit" 
                  className="newsletter-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
              {message.text && (
                <p className={`newsletter-message ${message.type}`}>
                  {message.text}
                </p>
              )}
            </div>

            {/* App Download Badges */}
            <div className="app-downloads">
              <div className="app-badge">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_playstore)">
                    <path d="M25.18 11.8989L20.4374 9.15967L15.582 14.015L20.437 18.8701L25.1961 16.1217C25.9383 15.6708 26.3811 14.8832 26.3811 14.015C26.3811 13.1469 25.9379 12.3592 25.18 11.8989Z" fill="#FFCE00"/>
                    <path d="M15.5815 14.0155L2.35652 0.695312C2.12215 0.927494 1.93614 1.20383 1.80924 1.50835C1.68233 1.81287 1.61706 2.13953 1.61719 2.46943V25.521C1.61719 26.211 1.89654 26.8352 2.35652 27.2955L15.5815 14.0155Z" fill="url(#paint0_linear_playstore)"/>
                    <path d="M15.5824 14.0166L20.4378 9.16123L20.4455 9.15353L13.9399 5.40786L5.36411 0.367666C4.59187 -0.108769 3.65545 -0.125222 2.86676 0.318307C2.68613 0.433128 2.5216 0.548298 2.35742 0.696024L15.5824 14.0166Z" fill="#00F076"/>
                    <path d="M2.35742 27.2938C2.5216 27.4415 2.68613 27.5567 2.86676 27.6715C3.26093 27.885 3.67191 28.0002 4.08253 28.0002C4.52606 28.0002 4.95314 27.8689 5.34766 27.6389L13.9396 22.6229L20.4452 18.8772L20.4375 18.8695L15.5824 14.0142L2.35742 27.2938Z" fill="#F63448"/>
                  </g>
                  <defs>
                    <linearGradient id="paint0_linear_playstore" x1="-4.29327" y1="20.6454" x2="9.00665" y2="7.34509" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#00E2FF"/>
                      <stop offset="1" stopColor="#00C5FF"/>
                    </linearGradient>
                    <clipPath id="clip0_playstore">
                      <rect width="28" height="28" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                <div className="app-badge-text">
                  <span className="app-badge-small">Get it on</span>
                  <span className="app-badge-large">Google Play</span>
                </div>
              </div>
              
              <div className="app-badge">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21.4643 26.233C20.0169 27.6367 18.4367 27.415 16.9155 26.7501C15.3057 26.0704 13.8288 26.0409 12.1304 26.7501C10.0037 27.6662 8.88122 27.4003 7.6111 26.233C0.403915 18.8007 1.46727 7.48234 9.6492 7.06861C11.643 7.17205 13.0313 8.16203 14.198 8.25069C15.9407 7.89606 17.6096 6.87653 19.4705 7.00951C21.7006 7.18682 23.3842 8.07338 24.4919 9.66917C19.884 12.4323 20.9769 18.5052 25.2008 20.2044C24.359 22.4208 23.2661 24.6224 21.4495 26.2477L21.4643 26.233ZM14.0503 6.97996C13.8288 3.68493 16.5019 0.966162 19.5739 0.700195C20.0022 4.51238 16.118 7.34936 14.0503 6.97996Z" fill="white"/>
                </svg>
                <div className="app-badge-text">
                  <span className="app-badge-small">Available on the</span>
                  <span className="app-badge-large">App Store</span>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="social-section">
              <span className="social-title">Follow Us</span>
              <div className="social-links">
                {/* Dynamic Social Links from Admin Panel */}
                {socialLinks.length > 0 ? (
                  socialLinks.map((link, index) => (
                    <a 
                      key={link.id || index} 
                      href={link.link || link.url || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-icon-wrapper"
                      title={link.name}
                    >
                      {link.icon ? (
                        <img src={link.icon} alt={link.name} style={{ width: 20, height: 20 }} />
                      ) : (
                        <span style={{ color: '#fff', fontSize: '12px' }}>{link.name?.charAt(0)?.toUpperCase()}</span>
                      )}
                    </a>
                  ))
                ) : (
                  <>
                    {/* Default Instagram */}
                    <div className="social-icon-wrapper">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_instagram)">
                          <path d="M19.9398 5.87707C19.8912 4.81262 19.7221 4.08566 19.475 3.44973C19.2239 2.78244 18.8303 2.17797 18.3215 1.67848C17.822 1.16968 17.2176 0.775957 16.5503 0.524766C15.9141 0.277695 15.1874 0.108789 14.1229 0.0604297C13.0565 0.0116016 12.7157 0 10 0C7.28426 0 6.94352 0.0116016 5.87707 0.0601562C4.81262 0.108789 4.08586 0.277852 3.44973 0.525039C2.78242 0.776115 2.17794 1.16976 1.67844 1.67852C1.16964 2.17795 0.775921 2.78237 0.524727 3.44961C0.277656 4.08574 0.10875 4.81266 0.0603906 5.87695C0.0116016 6.94352 0 7.2841 0 9.99984C0 12.7157 0.0116016 13.0565 0.0604297 14.1229C0.108945 15.1872 0.278008 15.9141 0.525195 16.5503C0.776268 17.2175 1.1699 17.822 1.67863 18.3214C2.17809 18.8302 2.78256 19.2238 3.44984 19.4748C4.08586 19.7221 4.81277 19.8911 5.87723 19.9397C6.94383 19.9884 7.28437 19.9999 10.0002 19.9999C12.7159 19.9999 13.0566 19.9884 14.1231 19.9397C15.1875 19.8911 15.9143 19.7221 16.5504 19.4748C17.2147 19.2179 17.818 18.8251 18.3216 18.3215C18.8253 17.8179 19.2181 17.2146 19.475 16.5503C19.7223 15.9141 19.8912 15.1872 19.9399 14.1229C19.9884 13.0563 20 12.7157 20 10C20 7.2841 19.9884 6.94352 19.9398 5.87707ZM18.1398 14.041C18.0954 15.0161 17.9325 15.5455 17.7956 15.8979C17.6292 16.3291 17.3744 16.7208 17.0475 17.0477C16.7207 17.3746 16.329 17.6294 15.8977 17.7957C15.5454 17.9326 15.0159 18.0956 14.0409 18.14C12.9866 18.1882 12.6703 18.1983 10 18.1983C7.32961 18.1983 7.01344 18.1882 5.95902 18.14C4.98418 18.0956 4.45473 17.9326 4.10223 17.7957C3.6679 17.6354 3.27502 17.3797 2.95246 17.0476C2.6203 16.7251 2.36466 16.3322 2.20434 15.8979C2.06746 15.5455 1.90449 15.0161 1.86008 14.041C1.81203 12.9866 1.8018 12.6703 1.8018 10.0002C1.8018 7.32988 1.81203 7.01375 1.86008 5.95918C1.90461 4.98418 2.06746 4.45469 2.20434 4.10238C2.36471 3.66802 2.62034 3.27509 2.95246 2.95246C3.27502 2.62029 3.66797 2.3647 4.10238 2.20449C4.45473 2.06746 4.98418 1.90465 5.95922 1.86008C7.01359 1.81203 7.32992 1.8018 10 1.8018H9.99988C12.67 1.8018 12.9863 1.81203 14.0409 1.86023C15.0159 1.90465 15.5452 2.06762 15.8977 2.20449C16.332 2.36485 16.7249 2.62043 17.0475 2.95246C17.3796 3.27501 17.6353 3.66797 17.7955 4.10238C17.9325 4.45473 18.0955 4.98418 18.1398 5.95922C18.1879 7.01359 18.1981 7.32992 18.1981 10C18.1981 12.6704 18.188 12.9864 18.1398 14.041Z" fill="url(#paint0_linear_instagram)"/>
                          <path d="M10.0003 4.86475C7.1643 4.86475 4.86523 7.16389 4.86523 9.9999C4.86523 12.8359 7.1643 15.1349 10.0003 15.1349C12.8364 15.1349 15.1355 12.8359 15.1355 9.9999C15.1355 7.16389 12.8364 4.86475 10.0003 4.86475ZM10.0003 13.3332C8.15945 13.333 6.66699 11.8407 6.66715 9.99975C6.66715 8.15893 8.15945 6.66646 10.0004 6.66646C11.8414 6.66662 13.3337 8.15893 13.3337 9.99975C13.3337 11.8407 11.8412 13.3332 10.0003 13.3332Z" fill="url(#paint1_linear_instagram)"/>
                          <path d="M16.5387 4.66203C16.5387 5.32473 16.0014 5.86195 15.3388 5.86195C14.6759 5.86195 14.1387 5.32469 14.1387 4.66203C14.1387 3.99914 14.6759 3.46191 15.3388 3.46191C16.0014 3.46191 16.5388 3.99918 16.5388 4.66203H16.5387Z" fill="url(#paint2_linear_instagram)"/>
                        </g>
                        <defs>
                          <linearGradient id="paint0_linear_instagram" x1="1.67836" y1="18.3215" x2="18.3216" y2="1.6782" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#FFD600"/>
                            <stop offset="0.5" stopColor="#FF0100"/>
                            <stop offset="1" stopColor="#D800B9"/>
                          </linearGradient>
                          <linearGradient id="paint1_linear_instagram" x1="6.3693" y1="13.6309" x2="13.6314" y2="6.36877" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#FF6400"/>
                            <stop offset="0.5" stopColor="#FF0100"/>
                            <stop offset="1" stopColor="#FD0056"/>
                          </linearGradient>
                          <linearGradient id="paint2_linear_instagram" x1="14.4902" y1="5.51047" x2="16.1872" y2="3.81344" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#F30072"/>
                            <stop offset="1" stopColor="#E50097"/>
                          </linearGradient>
                          <clipPath id="clip0_instagram">
                            <rect width="20" height="20" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    </div>

                    {/* Default Facebook */}
                    <div className="social-icon-wrapper">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.08836 11.0333C8.02556 11.0333 6.70816 11.0337 6.10056 11.0331C5.78776 11.0329 5.67616 10.9205 5.67616 10.6057C5.67566 9.79712 5.67566 8.98852 5.67616 8.17992C5.67636 7.86912 5.79436 7.75052 6.10316 7.75032C6.71076 7.74992 8.02096 7.75012 8.08836 7.75012V5.98972C8.08876 5.19492 8.23056 4.43412 8.63436 3.73752C9.04776 3.02452 9.64936 2.53612 10.4204 2.25372C10.9142 2.07272 11.4268 2.00052 11.9508 2.00012C12.6064 1.99972 13.262 2.00032 13.9178 2.00152C14.1996 2.00192 14.3246 2.12652 14.3252 2.41032C14.3264 3.17092 14.3264 3.93152 14.3252 4.69192C14.3248 4.97872 14.205 5.09392 13.9168 5.09712C13.3794 5.10292 12.8416 5.09932 12.3048 5.12092C11.7626 5.12092 11.4774 5.38572 11.4774 5.94692C11.4644 6.54052 11.472 7.13472 11.472 7.74992C11.5228 7.74992 13.0658 7.74972 13.787 7.74992C14.1146 7.74992 14.2266 7.86252 14.2266 8.19192C14.2266 8.99612 14.2264 9.80052 14.2258 10.6047C14.2256 10.9293 14.1204 11.0329 13.7908 11.0331C13.0696 11.0335 11.5326 11.0333 11.4654 11.0333V17.5423C11.4654 17.8893 11.3562 17.9999 11.0138 17.9999H8.50916C8.20656 17.9999 8.08856 17.8823 8.08856 17.5797L8.08836 11.0333Z" fill="#3D6AD6"/>
                      </svg>
                    </div>

                    {/* Default X (Twitter) */}
                    <div className="social-icon-wrapper social-icon-light">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="32" height="32" rx="16" fill="white"/>
                        <path d="M8 24L14.7096 17.2904M14.7096 17.2904L8 8H12.4444L17.2904 14.7096M14.7096 17.2904L19.5555 24H24L17.2904 14.7096M24 8L17.2904 14.7096" stroke="black" strokeWidth="1.38155" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>

                    {/* Default LinkedIn */}
                    <div className="social-icon-wrapper social-icon-light">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="32" height="32" rx="16" fill="white"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M11.5938 13.0156C11.5938 12.7137 11.3488 12.4688 11.0469 12.4688H7.54688C7.245 12.4688 7 12.7137 7 13.0156V24.1719C7 24.4737 7.245 24.7188 7.54688 24.7188H11.0469C11.3488 24.7188 11.5938 24.4737 11.5938 24.1719V13.0156ZM10.5 13.5625V23.625H8.09375V13.5625H10.5ZM9.29688 7C8.029 7 7 8.029 7 9.29688C7 10.5647 8.029 11.5938 9.29688 11.5938C10.5647 11.5938 11.5938 10.5647 11.5938 9.29688C11.5938 8.029 10.5647 7 9.29688 7ZM9.29688 8.09375C9.61035 8.1022 9.90814 8.23267 10.1269 8.45738C10.3456 8.68209 10.468 8.98329 10.468 9.29688C10.468 9.61046 10.3456 9.91166 10.1269 10.1364C9.90814 10.3611 9.61035 10.4915 9.29688 10.5C8.9834 10.4915 8.68561 10.3611 8.46688 10.1364C8.24815 9.91166 8.12576 9.61046 8.12576 9.29688C8.12576 8.98329 8.24815 8.68209 8.46688 8.45738C8.68561 8.23267 8.9834 8.1022 9.29688 8.09375ZM17.0625 13.3477V13.0156C17.0625 12.7137 16.8175 12.4688 16.5156 12.4688H13.0156C12.7137 12.4688 12.4688 12.7137 12.4688 13.0156V23.0011C12.4688 23.4567 12.6497 23.8936 12.9718 24.2157C13.2939 24.5378 13.7308 24.7188 14.1864 24.7188H16.5156C16.8175 24.7188 17.0625 24.4737 17.0625 24.1719V17.6181C17.0642 17.6037 17.0931 17.3709 17.3228 17.1754C17.5521 16.9807 17.9388 16.8438 18.5662 16.8438C19.7728 16.8438 19.9084 17.346 19.9084 17.346L20.4531 17.9375C20.1512 17.9375 19.9062 17.6925 19.9062 17.3906V24.1719C19.9062 24.4737 20.1512 24.7188 20.4531 24.7188H23.9531C24.255 24.7188 24.5 24.4737 24.5 24.1719V16.5642C24.5025 16.5218 24.5006 16.4793 24.4943 16.4373C24.3797 15.6335 24.058 14.8734 23.5607 14.2316C23.0634 13.5898 22.4077 13.0884 21.658 12.7767C20.9083 12.465 20.0904 12.3538 19.2847 12.4539C18.479 12.554 17.7131 12.862 17.0625 13.3477ZM23.4062 16.5568V23.625H21V17.4204C21.0101 17.3027 21.0149 16.9002 20.6977 16.5025C20.4221 16.1573 19.8468 15.75 18.5662 15.75C17.5713 15.75 16.9776 16.0331 16.6141 16.3424C15.9622 16.8967 15.9688 17.5998 15.9688 17.5998V23.625H14.1864C14.1044 23.6251 14.0233 23.609 13.9476 23.5776C13.8718 23.5463 13.803 23.5003 13.7451 23.4424C13.6872 23.3845 13.6412 23.3157 13.6099 23.2399C13.5785 23.1642 13.5624 23.0831 13.5625 23.0011V13.5625H15.9688V14.5469C15.9688 14.6525 15.9993 14.7558 16.0568 14.8444C16.1143 14.933 16.1961 15.0031 16.2926 15.0461C16.389 15.0892 16.4958 15.1035 16.6001 15.0871C16.7045 15.0708 16.8018 15.0246 16.8805 14.9542L17.4668 14.4288C17.948 13.9971 18.5371 13.7036 19.1715 13.5792C19.8059 13.4549 20.4622 13.5043 21.0708 13.7224C21.6794 13.9405 22.2177 14.319 22.6289 14.8179C23.04 15.3169 23.3086 15.9177 23.4062 16.5568Z" fill="#003654"/>
                      </svg>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="footer-divider-line"></div>
        <p className="copyright">© 2025 gulf Constuctor. All rights reserved.</p>
      </div>
    </footer>
  );
};

export default Footer;
